import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  type IMergeConflictDiff,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  Patient,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IPatient,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  WithRef,
  asDocRef,
  toTimestamp,
  FirestoreMigrate,
} from '@principle-theorem/shared';
import { omit } from 'lodash';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslationMapHandler } from '../../translation-map';
import { PreloadTranslationMap } from '../../translation-map/preload-translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import {
  DESTINATION_OWNED_PATIENT_KEYS,
  MigratedPatient,
} from './migrated-patient';

export const PATIENT_RESOURCE_TYPE = 'patients';

export interface IPatientDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  patientRef: DocumentReference<IPatient>;
}

export interface IPatientErrorData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
}

export interface IPatientJobData<PatientRecord extends ISourceEntityTypes> {
  sourcePatient: IGetRecordResponse<PatientRecord>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  brand: WithRef<IBrand>;
}

export interface IPatientMigrationData {
  sourcePatientId: string;
  patient: IPatient;
  createdAt?: Timestamp;
}

export abstract class BasePatientDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
> extends BaseDestinationEntity<
  IPatientDestinationRecord,
  IPatientJobData<PatientRecord>,
  IPatientMigrationData
> {
  abstract patientSourceEntity: ISourceEntityHandler<PatientRecord>;

  get sourceCountComparison(): ISourceEntityHandler<PatientRecord> {
    return this.patientSourceEntity;
  }

  private static _buildMergeConflictDiff(
    existingPatient: WithRef<IPatient>,
    data: IPatientMigrationData
  ): IMergeConflictDiff<IPatientMigrationData> {
    const mergedPatient = MigratedPatient.mergeOnto(
      existingPatient,
      data.patient,
      data.createdAt
    );
    return {
      principle: {
        ...data,
        patient: Patient.init(
          omit(existingPatient, DESTINATION_OWNED_PATIENT_KEYS)
        ),
      },
      migration: {
        ...data,
        patient: Patient.init(
          omit(mergedPatient, DESTINATION_OWNED_PATIENT_KEYS)
        ),
      },
    };
  }

  sourceCountDataAccessor(
    data: IPatientJobData<PatientRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return Firestore.doc$(record.data.patientRef).pipe(
      map((patient) => [
        {
          label: 'Patient',
          data: patient,
        },
      ])
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IPatientMigrationData
  ): Promise<IPatientMigrationData | undefined> {
    const existingPatientRef = await translationMap.getDestination(
      data.sourcePatientId,
      this.patientSourceEntity.sourceEntity.metadata.idPrefix
    );

    if (!existingPatientRef) {
      return;
    }

    try {
      const existingPatient = await Firestore.getDoc(
        asDocRef<IPatient>(existingPatientRef)
      );
      const diff = BasePatientDestinationEntity._buildMergeConflictDiff(
        existingPatient,
        data
      );

      // referenceId is last-run-wins, so it differs on every patient another
      // migration wrote and would make each linked patient a conflict.
      const hasMergeConflict = hasMergeConflicts(
        omit(diff.migration.patient, 'referenceId'),
        omit(diff.principle.patient, 'referenceId')
      );

      if (hasMergeConflict) {
        return diff.principle;
      }
    } catch {
      return;
    }
  }

  async buildMergeConflictDiff(
    translationMap: TranslationMapHandler,
    data: IPatientMigrationData
  ): Promise<IMergeConflictDiff<IPatientMigrationData> | undefined> {
    const existingPatientRef = await translationMap.getDestination(
      data.sourcePatientId,
      this.patientSourceEntity.sourceEntity.metadata.idPrefix
    );
    const existingPatient = existingPatientRef
      ? await Firestore.safeGetDoc(asDocRef<IPatient>(existingPatientRef))
      : undefined;

    if (!existingPatient) {
      return;
    }
    return BasePatientDestinationEntity._buildMergeConflictDiff(
      existingPatient,
      data
    );
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientJobData<PatientRecord>,
    _migrationData: IPatientMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  getDestinationEntityRecordUid(data: IPatientJobData<PatientRecord>): string {
    return data.sourcePatient.record.uid;
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IPatientJobData<PatientRecord>,
    migrationData: IPatientMigrationData
  ): Promise<IDestinationEntityRecord> {
    const resourceType =
      this.patientSourceEntity.sourceEntity.metadata.idPrefix;

    const patientRef = await Firestore.runTransaction(async (transaction) => {
      const mappedPatientRef = await PreloadTranslationMap.preload(
        translationMap,
        resourceType,
        Brand.patientCol(jobData.brand),
        migrationData.sourcePatientId
      );

      const existingPatient = await Firestore.safeGetDoc(
        mappedPatientRef,
        transaction
      );

      if (!existingPatient) {
        return FirestoreMigrate.upsertDoc(
          Brand.patientCol(jobData.brand),
          {
            ...migrationData.patient,
            createdAt: migrationData.createdAt,
          },
          mappedPatientRef,
          transaction
        );
      }

      await FirestoreMigrate.updateDoc(
        mappedPatientRef,
        MigratedPatient.mergeOnto(
          existingPatient,
          migrationData.patient,
          migrationData.createdAt
        ),
        transaction
      );
      return mappedPatientRef;
    });

    return this._buildSuccessResponse(jobData.sourcePatient.record, patientRef);
  }

  buildSkippedResponse(
    data: IPatientJobData<PatientRecord>
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<PatientRecord>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage: errorMessage ?? `Can't resolve patient data`,
      failData: {
        patientRef: patient.id,
      },
    };
  }

  protected _buildSuccessResponse(
    record: IGetRecordResponse<PatientRecord>['record'],
    patientRef: DocumentReference<IPatient>
  ): IDestinationEntityRecord<IPatientDestinationRecord> {
    return {
      uid: record.uid,
      label: record.label,
      data: {
        sourceRef: record.id,
        patientRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: record.id,
      migratedAt: toTimestamp(),
    };
  }
}
